// src/pages/Seasons.tsx — region × month multipliers.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ops, REGION_LABEL, type Region, type SeasonGrid } from "../lib/ops";
import { MONTHS } from "../lib/format";
import { useServices } from "../lib/context";
import { ChangePanel, type Pending } from "../components/ChangePanel";
import { Field, Loading, Notice, PageFrame } from "../components/ui";

const REGIONS: Region[] = ["pnw", "norcal"];

export default function Seasons() {
  const services = useServices();
  const [data, setData] = useState<SeasonGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<{ region: Region; month: number } | null>(null);
  const [value, setValue] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await ops.listSeasonMultipliers());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const mult = (r: Region, m: number) => data?.grid?.find((g) => g.region === r && g.month === m)?.multiplier ?? null;
  const pick = (region: Region, month: number) => {
    setSel({ region, month });
    setValue(String(mult(region, month) ?? "1"));
  };

  const n = Number(value);
  const valid = value.trim() !== "" && n >= 0.5 && n <= 1.5;
  const current = sel ? mult(sel.region, sel.month) : null;
  const exempt = data?.exempt_services ?? [];

  const panel = pending ? (
    <ChangePanel
      pending={pending}
      onClose={() => setPending(null)}
      onApplied={async () => {
        await Promise.all([load(), services.refresh()]);
      }}
    />
  ) : undefined;

  return (
    <PageFrame
      title="Seasonal multipliers"
      lede="Each month's price for seasonal services is base price × this multiplier. Tap a month to change it."
      panel={panel}
    >
      {exempt.length > 0 && (
        <Notice kind="info">
          {exempt.length} active services ignore these multipliers (flat all year):{" "}
          {exempt.map((s, i) => (
            <span key={s.id}>
              {i > 0 && ", "}
              <Link to={`/pricing/services?open=${encodeURIComponent(s.id)}`}>{s.name}</Link>
            </span>
          ))}
          .
        </Notice>
      )}
      {!data && !error && <Loading what="multipliers" />}
      {error && <Notice kind="error">{error}</Notice>}
      {data && (
        <div className="table-scroll">
          <table className="season-grid">
            <thead>
              <tr>
                <th scope="col">Region</th>
                {MONTHS.map((m) => (
                  <th key={m} scope="col">
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {REGIONS.map((r) => (
                <tr key={r}>
                  <th scope="row">{REGION_LABEL[r]}</th>
                  {MONTHS.map((name, i) => {
                    const m = i + 1;
                    const v = mult(r, m);
                    const on = sel?.region === r && sel.month === m;
                    const cls = v === null ? "" : v > 1 ? "up" : v < 1 ? "down" : "";
                    return (
                      <td key={m}>
                        <button
                          type="button"
                          className={`season-cell ${cls} ${on ? "selected" : ""}`}
                          aria-pressed={on}
                          aria-label={`${REGION_LABEL[r]} ${name}: ${v ?? "not set"}`}
                          onClick={() => pick(r, m)}
                        >
                          {v === null ? "—" : Number(v).toFixed(2)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <section className="editor-section card">
          <Field
            label={`${REGION_LABEL[sel.region]}, ${MONTHS[sel.month - 1]}`}
            htmlFor="mult"
            hint={`Now ${current ?? "not set"}. Between 0.50 and 1.50.`}
          >
            <input id="mult" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
          <button
            type="button"
            className="btn primary"
            disabled={!valid || n === current}
            onClick={() =>
              setPending({
                change: { type: "season_multiplier", region: sel.region, month: sel.month, multiplier: n },
                title: `${REGION_LABEL[sel.region]} ${MONTHS[sel.month - 1]} multiplier → ${n.toFixed(2)}`,
              })
            }
          >
            Preview multiplier change
          </button>
        </section>
      )}
    </PageFrame>
  );
}
